import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatHhMm, type FlightPlanFigures } from '@jupi/shared'

// The four answers a pilot keeps checking while editing -- how far, how
// long, how much fuel (trip and final reserve), and whether the loading is legal -- pinned to the
// bottom of the screen, just above the phone's bottom navigation.
export default function PlanSummaryBar({ figures, reserveMinutes }: { figures: FlightPlanFigures; reserveMinutes: number }) {
  const { t } = useTranslation()
  const { totals, fuel, weightBalance } = figures
  return (
    <Paper
      elevation={3}
      sx={{
        position: 'sticky',
        // Above the shell's 56 px bottom bar on a phone; the page foot on
        // a desktop.
        bottom: { xs: 'calc(64px + env(safe-area-inset-bottom))', md: 16 },
        zIndex: 3,
        px: 2,
        py: 1,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          alignItems: 'center',
          columnGap: 2,
          rowGap: 0.5,
          // A phone: the four figures two by two, the verdicts on a line below.
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, auto) 1fr' },
        }}
      >
        <Item label={t('plan.editor.dist')} value={`${totals.distanceNm.toFixed(1)} NM`} />
        <Item label={t('plan.editor.ete')} value={formatHhMm(totals.minutes)} />
        <Item label={t('plan.editor.fuelTrip')} value={fuel ? `${fuel.tripUsg.toFixed(1)} USG` : '–'} />
        <Item label={t('plan.weight.reserve')} value={fuel ? `${fuel.reserveUsg.toFixed(1)} USG` : '–'} note={fuel ? `(${reserveMinutes} min)` : undefined} />
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, flexWrap: 'wrap' }}
        >
          {weightBalance ? (
            <Chip
              size="small"
              color={weightBalance.ok ? 'success' : 'error'}
              label={weightBalance.ok ? t('plan.editor.wbOk') : t('plan.editor.wbNotOk')}
            />
          ) : null}
          {fuel && !fuel.sufficient ? <Chip size="small" color="error" label={t('plan.editor.fuelShort')} /> : null}
        </Stack>
      </Box>
    </Paper>
  )
}

function Item({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>
        {value}
        {note ? (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontFamily: 'inherit', fontWeight: 400 }}>
            {note}
          </Typography>
        ) : null}
      </Typography>
    </Box>
  )
}
